import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useWebSocket } from '../hooks/useWebSocket';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';
const POLL_MS = 30000;

const TEMP_COLOR = '#f97316';
const HUM_COLOR = '#06b6d4';

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' });
}

function fmtDateTime(ts) {
  if (!ts) return '–';
  const d = new Date(ts);
  return d.toLocaleString('sq-AL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function StatusDot({ status }) {
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
  );
}

function SeverityBadge({ severity }) {
  if (!severity) return null;
  const cls = severity === 'WARNING'
    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  return <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{severity}</span>;
}

const StatCard = ({ label, value, sub, color, icon }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl ${color}`}>
      {icon}
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value ?? '–'}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      {sub != null && <p className="text-xs text-gray-400 dark:text-gray-500">{sub}</p>}
    </div>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  const [stats, setStats] = useState(null);
  const [sensors, setSensors] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [chartData, setChartData] = useState({});  // { sensor_id: [{ts, temperature, humidity}] }
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState({});  // id → name

  const pollRef = useRef(null);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  }), []);

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, sensorsRes, alarmsRes, companiesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboard/stats`, { headers: headers() }),
        axios.get(`${BACKEND_URL}/api/sensors`, { headers: headers() }),
        axios.get(`${BACKEND_URL}/api/alarms?resolved=false&limit=5`, { headers: headers() }),
        axios.get(`${BACKEND_URL}/api/companies`, { headers: headers() }),
      ]);
      setStats(statsRes.data);
      const sensorList = Array.isArray(sensorsRes.data) ? sensorsRes.data : [];
      setSensors(sensorList);
      setAlarms(Array.isArray(alarmsRes.data) ? alarmsRes.data : []);
      const cMap = {};
      (Array.isArray(companiesRes.data) ? companiesRes.data : []).forEach(c => { cMap[c.id] = c.name; });
      setCompanies(cMap);

      // Select first sensor for chart if none selected
      if (!selectedSensor && sensorList.length > 0) {
        setSelectedSensor(sensorList[0].id);
      }
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [headers, selectedSensor]);

  const fetchChart = useCallback(async (sensorId) => {
    if (!sensorId) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/readings/${sensorId}?hours=24`, { headers: headers() });
      const raw = Array.isArray(res.data) ? res.data : [];
      const data = raw.map(r => ({
        ts: r.ts,
        time: fmtTime(r.ts),
        temperature: r.temperature != null ? parseFloat(r.temperature.toFixed(1)) : null,
        humidity: r.humidity != null ? parseFloat(r.humidity.toFixed(1)) : null,
      }));
      setChartData(prev => ({ ...prev, [sensorId]: data }));
    } catch (e) {
      console.error('Chart fetch error:', e);
    }
  }, [headers]);

  useEffect(() => {
    fetchAll();
    pollRef.current = setInterval(fetchAll, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchAll]);

  useEffect(() => {
    if (selectedSensor) fetchChart(selectedSensor);
  }, [selectedSensor, fetchChart]);

  // WebSocket — update-on sensorin live kur vjen reading i ri nga Pi
  useWebSocket(useCallback((reading) => {
    const { sensor_id, temperature, humidity, ts } = reading;

    // Update sensorin në listë
    setSensors(prev => prev.map(s =>
      s.id === sensor_id
        ? { ...s, temperature, humidity, last_reading_ts: ts, status: 'online' }
        : s
    ));

    // Shto pikën te grafiku nëse ky sensor është i zgjedhur
    if (sensor_id === selectedSensor) {
      const point = {
        ts,
        time: fmtTime(ts),
        temperature: temperature != null ? parseFloat(temperature.toFixed(1)) : null,
        humidity: humidity != null ? parseFloat(humidity.toFixed(1)) : null,
      };
      setChartData(prev => ({
        ...prev,
        [sensor_id]: [...(prev[sensor_id] || []).slice(-287), point],  // max 24h @ 5min = 288 pika
      }));
    }
  }, [selectedSensor]));

  const currentChartData = chartData[selectedSensor] || [];
  const selectedSensorObj = sensors.find(s => s.id === selectedSensor);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full p-8">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-full">

        {/* Title */}
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('dashboardTitle')}</h1>
          <span className="text-xs text-gray-400 dark:text-gray-500">{t('monitoringPoints')}: {stats?.total_sensors ?? 0}</span>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <StatCard
            label={t('totalSensors')}
            value={stats?.total_sensors ?? 0}
            color="bg-blue-500"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg>}
          />
          <StatCard
            label={t('onlineSensors')}
            value={stats?.online_sensors ?? 0}
            sub={`${t('offlineSensors')}: ${stats?.offline_sensors ?? 0}`}
            color="bg-green-500"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728M8.464 15.536a5 5 0 010-7.072m7.072 0a5 5 0 010 7.072M12 12h.01" /></svg>}
          />
          <StatCard
            label={t('activeAlarms')}
            value={stats?.active_alarms ?? 0}
            color="bg-red-500"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>}
          />
          <StatCard
            label={t('warningAlarms')}
            value={stats?.warning_alarms ?? 0}
            color="bg-amber-500"
            icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
          />
        </div>

        {/* Temp & Humidity avg */}
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-orange-100 dark:bg-orange-900/30">
              <svg className="w-7 h-7 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 14.76V3.5a2.5 2.5 0 00-5 0v11.26a4.5 4.5 0 105 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-500">
                {stats?.avg_temperature != null ? `${stats.avg_temperature}°C` : '–'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('avgTemperature')}</p>
              {stats?.min_temperature != null && (
                <p className="text-xs text-gray-400">Min: {stats.min_temperature}°C | Max: {stats.max_temperature}°C</p>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-cyan-100 dark:bg-cyan-900/30">
              <svg className="w-7 h-7 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-cyan-500">
                {stats?.avg_humidity != null ? `${stats.avg_humidity}%` : '–'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('avgHumidity')}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Sensor List */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-white text-sm">{t('monitoringPoints')}</h2>
              <span className="text-xs text-gray-400">{sensors.length} {t('sensors')}</span>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-72 overflow-y-auto">
              {sensors.length === 0 ? (
                <p className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">{t('noData')}</p>
              ) : sensors.map(s => {
                const hasAlarm = alarms.some(a => a.sensor_id === s.id);
                const alarmSev = alarms.find(a => a.sensor_id === s.id)?.severity;
                return (
                  <div
                    key={s.id}
                    onClick={() => navigate(`/sensors/${s.id}`)}
                    className={`flex items-center px-4 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition ${selectedSensor === s.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                  >
                    <StatusDot status={s.status} />
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.name}</p>
                      <p className="text-xs text-gray-400 truncate">{companies[s.company_id] || '–'}</p>
                    </div>
                    <div className="flex items-center gap-3 ml-2">
                      {s.temperature != null && (
                        <span className="text-sm font-semibold text-orange-500">{s.temperature.toFixed(1)}°C</span>
                      )}
                      {s.humidity != null && (
                        <span className="text-sm font-semibold text-cyan-500">{s.humidity.toFixed(1)}%</span>
                      )}
                      {hasAlarm && <SeverityBadge severity={alarmSev} />}
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedSensor(s.id); }}
                        className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 whitespace-nowrap"
                      >
                        Grafik
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-white text-sm">{t('last24h')}</h2>
              {selectedSensorObj && (
                <span className="text-xs text-gray-400 truncate max-w-[150px]">{selectedSensorObj.name}</span>
              )}
            </div>
            <div className="p-3">
              {currentChartData.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-sm text-gray-400">{t('noData')}</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={currentChartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis
                      dataKey="time"
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      interval={Math.floor(currentChartData.length / 6)}
                    />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }}
                      labelStyle={{ color: '#f9fafb' }}
                      formatter={(val, name) => [
                        val != null ? `${val}${name === 'temperature' ? '°C' : '%'}` : '–',
                        name === 'temperature' ? 'Temperatura' : 'Lagështia'
                      ]}
                    />
                    <Legend formatter={(v) => v === 'temperature' ? 'Temperatura (°C)' : 'Lagështia (%)'} wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="temperature" stroke={TEMP_COLOR} dot={false} strokeWidth={2} connectNulls />
                    <Line type="monotone" dataKey="humidity" stroke={HUM_COLOR} dot={false} strokeWidth={2} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Recent Alarms */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-white text-sm">{t('recentAlarms')}</h2>
            <button
              onClick={() => navigate('/alarms')}
              className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400"
            >
              {t('alarms')} →
            </button>
          </div>
          {alarms.length === 0 ? (
            <div className="p-6 text-center">
              <svg className="w-10 h-10 text-green-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('noAlarms')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {alarms.map(alarm => (
                <div key={alarm.id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                  <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${alarm.severity === 'WARNING' ? 'bg-amber-500' : 'bg-red-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{alarm.sensor_name || '–'}</p>
                      <SeverityBadge severity={alarm.severity} />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{alarm.message}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0 mt-0.5">{fmtDateTime(alarm.ts)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
}
